<template>
    <div class="ainotebook">
      <Topbar
        :pdfLoaded="pdfLoaded"
        :isExporting="isExporting"
        :user="liffState"
        @pick="triggerPick"
        @download="downloadPdf"
      />
  
      <main class="content">
        <Sidebar
          :pdfLoaded="pdfLoaded"
          :pages="pages"
          :selectedFile="selectedFile"
          :ocrLang="ocrLang"
          :analyzedCount="analyzedCount"
          :currentPageNumber="currentPageNumber"
          @pick="triggerPick"
          @filePicked="handleFile"
          @goPage="goToPage"
          @analyze="analyzePage"
          @needThumb="renderThumb"
          @updateOcrLang="(v) => (ocrLang = v)"
        />
  
        <Viewer
          :pdfLoaded="pdfLoaded"
          :docTitle="docTitle"
          :pagesLen="pages.length"
          :currentPageNumber="currentPageNumber"
          :currentPage="currentPage"
          :currentPageBlocks="currentPageBlocksSorted"
          :selectedBlockId="selectedBlockId"
          :busyText="busyText"
          @analyze="analyzePage"
          @selectBlock="selectBlock"
          @needMain="renderMain"
          @goPage="goToPage"
          @openMobileEditor="openMobileEditorSmart"
        />
  
        <EditorPanel
          :selectedBlock="selectedBlock"
          :selectedColor="selectedColor"
          :hexDraft="hexDraft"
          :isOneLineSelected="isOneLineSelected"
          @close="clearSelection"
          @colorPick="onColorPick"
          @commitHex="commitHex"
          @reset="resetBlock"
          @apply="applyEdits"
        />
      </main>
  
      <MobilePageNav
        v-if="pdfLoaded && pages.length > 0"
        :current="currentPageNumber"
        :total="pages.length"
        @go="goToPage"
      />
  
      <MobileToolbar
        v-if="pdfLoaded && currentPageBlocksSorted.length > 0"
        :count="currentPageBlocksSorted.length"
        @open="openMobileEditorSmart"
      />
  
      <MobileEditorSheet
        v-if="mobileBlock"
        :open="showMobileEditor"
        :selectedBlock="mobileBlock"
        :selectedColor="selectedColor"
        :hexDraft="hexDraft"
        @close="closeMobileEditor"
        @open="openMobileEditorFromMobile"
        @colorPick="onColorPick"
        @commitHex="commitHex"
        @reset="resetBlock"
        @apply="onMobileApply"
      />
  
      <!-- hidden file input -->
      <input
        ref="fileInput"
        class="file-input"
        type="file"
        accept="application/pdf"
        @change="onPick"
      />
    </div>
  </template>
  
  <script setup lang="ts">
  import { computed, nextTick, ref, watch, onMounted  } from 'vue'
  import { useLiffAuth } from '@/composables/useLiffAuth'
  import Topbar from '@/components/ainotebook/Topbar.vue'
  import Sidebar from '@/components/ainotebook/Sidebar.vue'
  import Viewer from '@/components/ainotebook/Viewer.vue'
  import EditorPanel from '@/components/ainotebook/EditorPanel.vue'
  import MobilePageNav from '@/components/ainotebook/MobilePageNav.vue'
  import MobileToolbar from '@/components/ainotebook/MobileToolbar.vue'
  import MobileEditorSheet from '@/components/ainotebook/MobileEditorSheet.vue'
  
  import { useRootClass } from '@/composables/ainotebook/useRootClass'
  import { useNotebookState } from '@/composables/ainotebook/useNotebookState'
  import { usePdfDocument } from '@/composables/ainotebook/usePdfDocument'
  import { usePageRenderer } from '@/composables/ainotebook/usePageRenderer'
  import { useAnalyzePage } from '@/composables/ainotebook/useAnalyzePage'
  import { useExportPdf } from '@/composables/ainotebook/useExportPdf'
  import { useTextareaAutosize } from '@/composables/ainotebook/useTextareaAutosize'
  import { useColorBinding } from '@/composables/ainotebook/useColorBinding'
  import { useBlockApply } from '@/composables/ainotebook/useBlockApply'
  import type { PageState } from '@/types/ainotebook'
  
  useRootClass('ainotebook-root')
  
  // ---------- global notebook state ----------
  const {
    selectedFile, pages, pdfLoaded, currentPageNumber,
    selectedBlockId, showMobileEditor, ocrLang,
    docTitle, analyzedCount, currentPage, currentPageBlocks, selectedBlock,
  } = useNotebookState()
  
  const busyText = ref('處理中…')
  const isExporting = ref(false)
  
  // file input
  const fileInput = ref<HTMLInputElement | null>(null)
  function triggerPick() {
    fileInput.value?.click()
  }
  function onPick(e: Event) {
    const input = e.target as HTMLInputElement
    const f = input.files?.[0]
    if (!f) return
    if (f.type !== 'application/pdf') return
    handleFile(f)
    input.value = ''
  }
  
  // ---------- pdf ----------
  const { pdfDoc, loadFromFile, initPages, cleanupPdf } = usePdfDocument()
  
  const { renderThumb, renderMain, cancelAllRenders } = usePageRenderer(pdfDoc, pages)
  
  // ---------- analyze ----------
  const { analyzePage, cancelAnalyze } = useAnalyzePage({
    pdfDoc,
    pages,
    ocrLang,
    busyText,
    selectedBlockId,
    showMobileEditor,
    renderMain,
  })
  
  // ---------- export ----------
  const { downloadPdf } = useExportPdf({ pdfDoc, pages, isExporting })
  
  const { state: liffState, initAndRequireLogin } = useLiffAuth()

  // ✅ mobileBlock：就算 selectedBlockId 被清掉，也還有東西可以編輯 & 展開按鈕不會消失
  const mobileBlock = computed(() => {
    return selectedBlock.value ?? currentPageBlocksSorted.value[0] ?? null
  })

  // ✅ 點「展開」：確保 selectedBlockId 有值，否則 selectedBlock 可能是 null
  function openMobileEditorFromMobile() {
    const b = mobileBlock.value
    if (!b) return
    selectedBlockId.value = b.id
    showMobileEditor.value = true
  }

  // ✅ 套用：先確保有選中 block，再 apply，apply 完收合
  async function onMobileApply() {
    const b = mobileBlock.value
    if (!b) return

    // 保底：避免 applyEdits 找不到 selectedBlock
    const keepId = b.id
    if (!selectedBlockId.value) selectedBlockId.value = keepId

    await applyEdits()

    // 有些流程會把 selectedBlockId 清掉，這裡把它補回來，確保還能再展開
    if (!selectedBlockId.value) selectedBlockId.value = keepId

    showMobileEditor.value = false
  }

  onMounted(async () => {
    await initAndRequireLogin()
    // 到這裡已確保登入完成（否則已跳轉 login）
  })
  // ---------- editor helpers ----------
  function selectBlock(id: string) {
    selectedBlockId.value = id
    showMobileEditor.value = true

    const p = pages.value.find(x => x.pageNumber === currentPageNumber.value)
    if (p) p.lastSelectedBlockId = id
  }
  function clearSelection() {
    selectedBlockId.value = ''
  }
  function closeMobileEditor() {
    showMobileEditor.value = false
  }
  
  // mobile toolbar behavior: if none selected, pick firstHigh or first
  function openMobileEditorSmart() {
    if (selectedBlockId.value) {
      showMobileEditor.value = true
      return
    }
    const blocks = currentPageBlocksSorted.value
    if (!blocks.length) return
    const firstHigh = blocks.find(b => (b.confidence ?? -1) >= 90)
    selectedBlockId.value = (firstHigh ?? blocks[0]).id
    const p = pages.value.find(x => x.pageNumber === currentPageNumber.value)
    if (p) p.lastSelectedBlockId = selectedBlockId.value
    showMobileEditor.value = true
  }
  
  // sort blocks: selected first, then firstHigh, then rest (like your original)
  const currentPageBlocksSorted = computed(() => {
    const blocks = currentPageBlocks.value.slice()
    if (!blocks.length) return blocks
    const sel = selectedBlockId.value
    if (sel) {
      const idx = blocks.findIndex(b => b.id === sel)
      if (idx !== -1) return [blocks[idx], ...blocks.slice(0, idx), ...blocks.slice(idx + 1)]
    }
    const firstHighIdx = blocks.findIndex(b => (b.confidence ?? -1) >= 90)
    if (firstHighIdx !== -1) return [blocks[firstHighIdx], ...blocks.slice(0, firstHighIdx), ...blocks.slice(firstHighIdx + 1)]
    return blocks
  })
  
  // ---------- textarea autosize ----------
  const editorTaRef = ref<HTMLTextAreaElement | null>(null)
  const isOneLineSelected = computed(() => {
    const b = selectedBlock.value
    if (!b) return false
    return b.rect.h <= 32
  })
  useTextareaAutosize(editorTaRef, computed(() => selectedBlock.value?.text), isOneLineSelected, selectedBlockId)
  
  // ---------- color binding (draft + normalize) ----------
  const { selectedColor, hexDraft, onColorPick, commitHex } = useColorBinding(selectedBlock)
  
  // ---------- apply edits / rerender ----------
  const { applyEdits, resetBlock: resetBlockById } = useBlockApply({
    pdfDoc,
    currentPage,
    renderMain, // we use a safe rerender flow from original PDF
    busyText,
  })

  // ✅ Wrapper function for template usage
  async function resetBlock() {
    const b = selectedBlock.value
    if (!b) return
    await resetBlockById(b.id)
  }
  
  // ---------- routing-ish behaviors ----------
  async function goToPage(pageNumber: number) {
    // ✅ 1) 切走前：把目前頁面的選取記住
    const prevNum = currentPageNumber.value
    const prevPage = pages.value.find(x => x.pageNumber === prevNum)
    if (prevPage) {
      prevPage.lastSelectedBlockId = selectedBlockId.value || prevPage.lastSelectedBlockId || ''
    }

    // ✅ 2) 切到新頁
    currentPageNumber.value = pageNumber

    // ✅ render current page main (如果這頁沒有 pageUrl 就會自動重畫)
    const targetPage = pages.value.find(x => x.pageNumber === pageNumber)
    if (targetPage && !targetPage.pageUrl) {
      renderMain(pageNumber, 2.0, true).catch(() => {})
    } else {
      renderMain(pageNumber).catch(() => {})
    }

    await nextTick()

    const p = pages.value.find(x => x.pageNumber === pageNumber)
    if (!p) return

    // ✅ 3) 先嘗試還原這頁上次選的 block
    const restoreId = p.lastSelectedBlockId
    if (restoreId && p.blocks?.some(b => b.id === restoreId)) {
      selectedBlockId.value = restoreId
      showMobileEditor.value = true // 你希望回來時仍然在編輯狀態
      await nextTick()
      editorTaRef.value?.focus()
      return
    }

    // ✅ 4) 沒有記錄就走你原本的 fallback：firstHigh / first
    selectedBlockId.value = ''
    if (p.analyzed && p.blocks.length > 0) {
      const firstHigh = p.blocks.find(b => (b.confidence ?? -1) >= 90)
      selectedBlockId.value = (firstHigh ?? p.blocks[0]).id
      showMobileEditor.value = true
      await nextTick()
      editorTaRef.value?.focus()
    }
  }

  
  // ---------- handle file ----------
  async function handleFile(f: File) {
    // reset
    selectedFile.value = f
    selectedBlockId.value = ''
    pdfLoaded.value = false
    pages.value = []
    currentPageNumber.value = 1
  
    cancelAnalyze()
    cancelAllRenders()
    cleanupPdf()
  
    const { numPages } = await loadFromFile(f)
    pages.value = initPages(numPages)
    pdfLoaded.value = true
  
    // render current page main + lazy thumbs request from list via events
    renderMain(1).catch(() => {})
    // thumbs are requested on-demand by PagesList (IntersectionObserver)
  }
  
  // cleanup on file change (extra safety)
  watch(
    () => selectedFile.value,
    () => {
      cancelAnalyze()
      cancelAllRenders()
    }
  )
  </script>
  
  <style scoped lang="scss">
  .ainotebook {
    height: 100vh;
    background: #050a12;
    color: #e8eefc;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* ✅ 新增：LIFF 使用者顯示區（避免 avatar 撐爆） */
  /* ✅ 左上角使用者資訊：修正 avatar 壓到文字 */
  .user {
    display: inline-flex;
    align-items: center;
    gap: 10px;

    /* 如果你有做成 pill */
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(10,18,33,0.85);
    backdrop-filter: blur(10px);

    /* 關鍵：避免內容超寬把 topbar 撐爆 */
    max-width: 320px;
    min-width: 0;
  }

  /* ✅ 讓圖片「一定」占位，不會浮起來蓋到字 */
  .user .avatar {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    object-fit: cover;
    display: block;

    flex: 0 0 28px;      /* ✅ 佔位固定 */
  }

  /* ✅ 文字區要允許縮小 + ellipsis，避免擠到圖 */
  .user .name {
    min-width: 0;         /* ✅ 超重要：讓文字可縮 */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 手機再縮一點 */
  @media (max-width: 720px) {
    .user {
      max-width: 220px;
      padding: 5px 10px;
      gap: 8px;
    }
    .user .avatar {
      width: 24px;
      height: 24px;
      flex-basis: 24px;
    }
  }

  .file-input {
    display: none;
  }

  .content {
    flex: 1;
    height: calc(100vh - 64px);
    min-height: 0;
    display: grid;
    grid-template-columns: 320px 1fr 360px;
    gap: 12px;
    padding: 12px;
    overflow: hidden;
  }

  @media (max-width: 1100px) {
    .content {
      grid-template-columns: 320px 1fr;
    }
  }

  @media (max-width: 720px) {
    .content {
      height: calc(100vh - 56px);
      grid-template-columns: 1fr;
      padding: 0;
      gap: 0;
    }
  }

  .file-input {
    display: none;
  }
  
  .content {
    flex: 1;
    height: calc(100vh - 64px);
    min-height: 0;
    display: grid;
    grid-template-columns: 320px 1fr 360px;
    gap: 12px;
    padding: 12px;
    overflow: hidden;
  }
  
  @media (max-width: 1100px) {
    .content {
      grid-template-columns: 320px 1fr;
    }
  }
  
  @media (max-width: 720px) {
    .content {
      height: calc(100vh - 56px);
      grid-template-columns: 1fr;
      padding: 0;
      gap: 0;
    }
  }
  </style>
  